import { create } from 'zustand';
import type { Role, SessionView } from '../lib/types.js';

/**
 * Who is signed in — the one piece of cross-cutting client state every
 * screen and the HTTP client read (CLAUDE.md: Zustand for shared client
 * state, narrow selectors). The server data behind it (`GET
 * /app/api/session`) lives in TanStack Query; this store keeps only the
 * derived context: signed-in status, the CSRF token the client must repeat
 * on mutations, and the role/organisation the shell renders.
 */
export type SessionStatus = 'unknown' | 'signed_out' | 'signed_in';

export interface SessionState {
  status: SessionStatus;
  csrfToken: string | null;
  role: Role | null;
  memberEmail: string | null;
  accountName: string | null;
  plan: string | null;
  signIn: (view: SessionView) => void;
  signOut: () => void;
}

export const useSessionStore = create<SessionState>()((set) => ({
  status: 'unknown',
  csrfToken: null,
  role: null,
  memberEmail: null,
  accountName: null,
  plan: null,
  signIn: (view) =>
    set({
      status: 'signed_in',
      csrfToken: view.csrf_token,
      role: view.member.role,
      memberEmail: view.member.email,
      accountName: view.account.company_name ?? view.account.email,
      plan: view.entitlements.plan,
    }),
  signOut: () =>
    set({
      status: 'signed_out',
      csrfToken: null,
      role: null,
      memberEmail: null,
      accountName: null,
      plan: null,
    }),
}));

export const canIssue = (role: Role | null): boolean =>
  role === 'admin' || role === 'verifier';
export const canManage = (role: Role | null): boolean => role === 'admin';
